import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { message } from 'antd';
import bridge from './bridge';
import { useNetwork } from './NetworkGate';
import { isNativeWebView } from '../utils/appNavigation';
import { setDraftSync } from '../redux/offline/offlineSlice';
import { saveDraftValue } from 'apis/GeneralApis/DynamicForm/FormValueAPI';

// Module 7.1 — Offline draft auto-sync sweep.
// When the device is ONLINE inside the native app AND the user enabled "Auto-sync offline drafts",
// push every offline-saved draft (pending_sync=1) to the server, then clear its flag. Native-only and
// best-effort: it no-ops in a plain browser (isNativeWebView() is false / the bridge call rejects), so
// browser and online-web behaviour is unchanged. Triggers on the offline→online transition and when the
// setting is turned on. A failed push leaves the draft pending for the next reconnect.
//
// Progress is reported to redux (offline.draftSync) so SyncProgressBar can show the drafts
// segment alongside tickets/forms, and a one-time message is shown when the sweep finishes.
export default function OfflineDraftAutoSync() {
  const { online } = useNetwork();
  const autoSync = useSelector((s) => s?.userReducer?.userData?.autoSyncOfflineDrafts);
  const dispatch = useDispatch();
  const running = useRef(false);

  useEffect(() => {
    if (!isNativeWebView() || !online || !autoSync || running.current) return;
    running.current = true;
    (async () => {
      try {
        const list = await bridge.call('storage.listPendingDrafts');
        const drafts = Array.isArray(list) ? list : [];
        if (!drafts.length) return;

        const total = drafts.length;
        let done = 0;
        let failed = 0;
        dispatch(setDraftSync({ active: true, total, done: 0, failed: 0 }));

        for (const d of drafts) {
          try {
            const syncRes = await saveDraftValue(d.draft);
            // Bug 5 — saveDraftValue catches HTTP errors and returns them without throwing, so we must
            // check isSuccess explicitly before clearing the local pending flag. If the server rejected
            // the draft, leave pending_sync=1 so it is retried on the next reconnect.
            if (!syncRes?.data?.isSuccess) {
              throw new Error(syncRes?.data?.displayErrorMessage || 'Server rejected draft');
            }
            await bridge.call('storage.markDraftSynced', {
              ticket_id: d.ticket_id,
              form_id: d.form_id,
              attachment_instance_id: d.attachment_instance_id || undefined,
            });
          } catch (e) {
            // leave pending_sync=1 → retried on the next reconnect
            failed += 1;
          }
          done += 1;
          dispatch(setDraftSync({ done, failed }));
        }

        dispatch(setDraftSync({ active: false }));
        const synced = total - failed;
        if (failed === 0) {
          message.success(`All offline drafts synced (${synced}/${total}).`);
        } else {
          message.warning(
            `${synced} of ${total} offline drafts synced; the rest will retry on the next reconnect.`
          );
        }
      } catch (e) {
        // bridge unavailable / not native → no-op
        dispatch(setDraftSync({ active: false }));
      } finally {
        running.current = false;
      }
    })();
  }, [online, autoSync, dispatch]);

  return null;
}
